import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getProducts } from '../../../lib/wp-api';
import { useCart } from '../../../context/CartContext';
import { useWishlist } from '../../../context/WishlistContext';

// Map a WC Store API product into the shape this component already expects.
const fromWc = (p) => {
  const minor = p?.prices?.currency_minor_unit ?? 2;
  const priceCents = parseInt(p?.prices?.price ?? '0', 10);
  return {
    id: p?.id,
    name: p?.name,
    brand: '',
    price: priceCents / Math.pow(10, minor),
    rating: parseFloat(p?.average_rating ?? '0') || 0,
    reviews: p?.review_count ?? 0,
    image: p?.images?.[0]?.thumbnail || p?.images?.[0]?.src || '',
    alt: p?.images?.[0]?.alt || p?.name,
    badge: p?.on_sale ? 'Sale' : null,
  };
};

const TrendingProducts = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const wishlist = useWishlist();

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    cart.addItem(product, 1);
  };
  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    e.preventDefault();
    wishlist.toggle(product);
  };
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getProducts({ per_page: 8, tag: 'trending' })
      .then((rows) => { if (!cancelled) setProducts(rows.map(fromWc)); })
      .catch(() => { if (!cancelled) setProducts([]); });
    return () => { cancelled = true; };
  }, []);


  const handleProductClick = (product) => {
    navigate(`/product-details?id=${product?.id}`);
  };

  const handleViewAll = () => {
    navigate('/product-catalog');
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-2">

              Trending Now
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-muted-foreground">

              Most loved products by style enthusiasts
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}>

            <Button
              onClick={handleViewAll}
              variant="outline"
              className="hidden md:flex items-center gap-2">

              View All
              <Icon name="ArrowRight" size={16} />
            </Button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products?.map((product, index) =>
          <motion.div
            key={product?.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -8 }}
            onClick={() => handleProductClick(product)}
            className="cursor-pointer group">

              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="relative overflow-hidden aspect-square">
                  <img
                  src={product?.image}
                  alt={product?.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                  {product?.badge && (
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      {product?.badge}
                    </span>
                  </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleToggleWishlist(e, product)}
                    aria-label={wishlist.has(product?.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm rounded-full p-2 hover:bg-background transition-colors"
                  >
                    <Icon
                      name="Heart"
                      size={20}
                      className={wishlist.has(product?.id) ? 'text-error fill-current' : 'text-foreground'}
                    />
                  </button>
                </div>
                <div className="p-5">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {product?.brand}
                  </p>
                  <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                    {product?.name}
                  </h3>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-amber-500 fill-current" />
                      <span className="text-sm font-medium">{product?.rating}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ({product?.reviews})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-foreground">
                      ${product?.price}
                    </span>
                    <Button size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => handleAddToCart(e, product)}>Add to Cart</Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-8 text-center md:hidden">

          <Button
            onClick={handleViewAll}
            variant="outline"
            className="w-full sm:w-auto">

            View All Products
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>);

};

export default TrendingProducts;
